import React from "react";
import "./instructorSignups.css";
import Icon from "./Icon";
import SlotGroup from "./SlotGroup";
import { approveOpeningSignup, unapproveOpeningSignup } from "../../../api/backend";
import WholeClassRequests from "./WholeClassRequests";
import { roleLabel, filterGroups, sortGroups, countGroups, buildClusters } from "./signupsModel";

const SUBS = [["pending", "Pending"], ["decided", "Decided"], ["all", "All"]];
const NO_FILTERS = { attention: false, course: "", role: "", query: "" };

function Empty({ icon = "inbox", title, children, action, tone }) {
  return (
    <div className={`isg-empty${tone === "err" ? " err" : ""}`}>
      <div className="ic"><Icon name={icon} size={24} /></div>
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}

function Skeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="isg-sr">Loading sign-ups</span>
      {[0, 1, 2].map((i) => (
        <div className="isg-sk" key={i} aria-hidden="true"><i className="b" /><div><i className="l" /><i className="s" /></div><i /><i /><i /></div>
      ))}
    </div>
  );
}

const apiMessage = (e, fallback) => e?.response?.data?.message || fallback;

/**
 * Course Approvals > Instructor Sign-ups. The coordinator's cross-course inbox: one group per open slot, applicants
 * side by side with their eligibility, Approve (assigns) / Unapprove per applicant. `groups` come from
 * GET /api/v1/instructor-signups; every decision calls onReload so the list reflects the server.
 * Approve and unapprove send `tenantId` as X-Tenant-Id.
 */
export default function InstructorSignupsTab({ groups, loading, error, onReload, tenantId, now }) {
  const [sub, setSub] = React.useState("pending");
  const [filters, setFilters] = React.useState(NO_FILTERS);
  const [sort, setSort] = React.useState("soon");
  const [uiById, setUiById] = React.useState({});
  const [live, setLive] = React.useState("");
  const nowMs = now ?? Date.now();

  const ui = (g) => uiById[g.openingId] || {};
  const patch = (g, p) => setUiById((m) => ({ ...m, [g.openingId]: { ...(m[g.openingId] || {}), ...p } }));
  const announce = (text) => { setLive(""); setTimeout(() => setLive(text), 30); };

  const counts = countGroups(groups, nowMs);
  const courses = [...new Set(groups.map((g) => g.courseTitle).filter(Boolean))].sort();
  const roles = [...new Set(groups.map((g) => g.teachingRole))];
  const visible = sortGroups(filterGroups(groups, { sub, ...filters }, nowMs), sort);
  const filtering = filters.attention || filters.course || filters.role || filters.query;

  const approve = (g, a) => patch(g, { confirm: a.id, err: null });
  const go = async (g) => {
    const signup = g.applicants.find((a) => a.id === ui(g).confirm);
    if (!signup) return;
    patch(g, { confirm: null, flight: signup.id });
    announce(`Assigning ${signup.instructorName}…`);
    try {
      await approveOpeningSignup(g.sessionId, g.openingId, signup.id, tenantId);
      patch(g, { flight: null, open: false });
      announce(`${signup.instructorName} assigned as ${roleLabel(g.teachingRole).toLowerCase()}. Other applicants stay pending.`);
    } catch (e) {
      const msg = apiMessage(e, "Please try again.");
      patch(g, { flight: null, err: { a: signup.id, msg } });
      announce(`Couldn't assign ${signup.instructorName}. ${msg}`);
    }
    onReload();
  };
  // Rejecting asks first (with an optional comment for the instructor) instead of acting on the click.
  const askReject = (g, a, reopen) => patch(g, { reject: a.id, rejectReopen: Boolean(reopen), note: "", confirm: null, err: null, ...(reopen ? { open: true } : {}) });
  const rejectGo = (g) => {
    const target = g.applicants.find((a) => a.id === ui(g).reject);
    if (target) unapprove(g, target, ui(g).rejectReopen, ui(g).note);
  };
  const unapprove = async (g, a, reopen, note) => {
    patch(g, { flight: a.id, confirm: null, reject: null, note: "" });
    try {
      await unapproveOpeningSignup(g.sessionId, g.openingId, a.id, tenantId, note);
      patch(g, { flight: null, ...(reopen ? { open: true } : {}) });
      announce(`${a.instructorName} marked not approved.${reopen ? " The slot is open again." : ""}`);
    } catch (e) {
      patch(g, { flight: null, err: { a: a.id, msg: apiMessage(e, "Could not change this sign-up.") } });
    }
    onReload();
  };

  const setFilter = (p) => setFilters((f) => ({ ...f, ...p }));
  const clear = () => setFilters(NO_FILTERS);

  let body;
  if (loading && groups.length === 0) body = <Skeleton />;
  else if (error && groups.length === 0) {
    body = (
      <Empty icon="wifi" tone="err" title="Couldn't load sign-ups"
        action={<button type="button" className="isg-btn isg-btn-d" onClick={onReload}><Icon name="retry" size={13} />Retry</button>}>
        Check your connection and try again. Decisions you already made are saved.
      </Empty>
    );
  } else if (visible.length === 0 && filtering) {
    body = (
      <Empty title="No slots match these filters" action={<button type="button" className="isg-btn isg-btn-o" onClick={clear}>Clear filters</button>}>
        Try a different course or role, or clear the search.
      </Empty>
    );
  } else if (visible.length === 0 && sub === "pending") {
    body = (
      <Empty title="Nothing waiting on you" action={<button type="button" className="isg-btn isg-btn-o" onClick={() => setSub("decided")}>View decided</button>}>
        New sign-ups appear here when instructors apply for an open slot.
      </Empty>
    );
  } else if (visible.length === 0) {
    body = <Empty title="No decisions yet">Slots you fill appear here.</Empty>;
  } else {
    body = visible.map((g) => (
      <SlotGroup key={g.openingId} group={g} ui={ui(g)} now={nowMs}
        onToggle={() => patch(g, { open: !(ui(g).open ?? g.status !== "FILLED") })}
        onApprove={(a) => approve(g, a)} onUnapprove={(a) => askReject(g, a, false)}
        onGo={() => go(g)} onCancel={() => patch(g, { confirm: null, reject: null })} onDismiss={() => patch(g, { err: null })}
        onUndo={(filler) => askReject(g, filler, true)}
        onNote={(text) => patch(g, { note: text })} onRejectGo={() => rejectGo(g)} />
    ));
  }

  return (
    <div className="isg-shell">
      <div className="isg-tb">
        <div className="isg-pills" role="group" aria-label="Status">
          {SUBS.map(([key, label]) => (
            <button type="button" key={key} className={sub === key ? "on" : ""} aria-pressed={sub === key} onClick={() => setSub(key)}>
              {label} <span className="isg-cnt">{counts[key]}</span>
            </button>
          ))}
        </div>
        <label className="isg-search">
          <Icon name="search" size={15} />
          <input type="search" placeholder="Search course or instructor" aria-label="Search course or instructor"
            value={filters.query} onChange={(e) => setFilter({ query: e.target.value })} />
        </label>
      </div>
      <div className="isg-tb isg-tb2">
        <button type="button" className="isg-chip" aria-pressed={filters.attention} onClick={() => setFilter({ attention: !filters.attention })}>
          <Icon name="alertC" size={14} />Needs attention <span className="isg-cnt">{counts.attention}</span>
        </button>
        <label className="isg-sel">Course
          <select value={filters.course} onChange={(e) => setFilter({ course: e.target.value })}>
            <option value="">All courses</option>
            {courses.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="isg-sel">Role
          <select value={filters.role} onChange={(e) => setFilter({ role: e.target.value })}>
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </label>
        <label className="isg-sel">Sort
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="soon">Soonest session</option>
            <option value="wait">Longest waiting</option>
            <option value="course">Course</option>
            <option value="role">Role</option>
          </select>
        </label>
      </div>
      {sub !== "decided" && !loading && (
        <WholeClassRequests clusters={buildClusters(visible)} tenantId={tenantId} onReload={onReload} announce={announce} />
      )}
      <div>{body}</div>
      <div className="isg-sr" aria-live="polite">{live}</div>
    </div>
  );
}
